import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Card } from "../Card";
import styles from "./PrimaryCard.module.scss";

const PrimaryCard = ({
	className,
	style,
	image,
	logo,
	logoPosition,
	header,
	subheader,
	content,
	button,
	link,
	color,
	renderImage,
	renderLink,
}) => (
	<Card className={cx(styles.primaryCard, className)} style={style}>
		<div className={styles.primaryCardImageContainer}>
			<Card.Image
				className={styles.primaryCardImage}
				src={image?.src}
				alt={image?.alt}
				imageComponentProps={{ renderImage }}
			/>
			{logo && (
				<Card.Logo logo={logo} logoPosition={logoPosition} imageComponentProps={{ renderImage }} />
			)}
		</div>
		<Card.Body className={styles.primaryCardBody}>
			<span style={{ color }}>
				<Card.Header size="h4" className={styles.primaryCardHeading}>
					{header}
				</Card.Header>
			</span>
			<div className={styles.primaryCardBodyInner}>
				<div className={styles.primaryCardBodyText}>
					{subheader && (
						<Card.Subheader className={styles.primaryCardSubheading}>{subheader}</Card.Subheader>
					)}
					{content && (
						<Card.Content className={styles.content}>
							<span dangerouslySetInnerHTML={{ __html: content }} />
						</Card.Content>
					)}
				</div>
				{button?.to && (
					<Card.Button
						to={button?.to}
						target={button?.target}
						label={button?.text}
						size="sm"
						className={styles.primaryCardButton}
						linkComponentProps={{ renderLink }}>
						{button?.text}
					</Card.Button>
				)}
				{link?.to && link?.text && (
					<Card.Button
						size="sm"
						to={link.to}
						target={link?.target}
						linkComponentProps={{ renderLink }}>
						{link.text}
					</Card.Button>
				)}
			</div>
		</Card.Body>
		{link?.to && (
			<Card.AsLink
				link={link?.to}
				target={link?.target}
				text={header || link?.text}
				className={styles.primaryCardLink}
				renderLink={renderLink}
			/>
		)}
		{button?.to && (
			<Card.AsLink
				link={button?.to}
				target={button?.target}
				text={button?.text}
				className={styles.primaryCardLink}
				renderLink={renderLink}
			/>
		)}
	</Card>
);

PrimaryCard.propTypes = {
	className: PropTypes.string,
	style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	/**
	 * Required image
	 */
	image: PropTypes.shape({
		fluid: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
		fixed: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}).isRequired,
	/**
	 * Renders optional logo on card image
	 */
	logo: PropTypes.shape({
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}),
	logoPosition: PropTypes.oneOf(["top-left", "top-right", "bottom-left", "bottom-right"]),
	/**
	 * Required header
	 */
	header: PropTypes.string.isRequired,
	subheader: PropTypes.string,
	content: PropTypes.string,
	/**
	 * Renders if onClick is available
	 */
	button: PropTypes.shape({
		icon: PropTypes.string,
		iconPlacement: PropTypes.oneOf(["right", "left"]),
		onClick: PropTypes.func,
		text: PropTypes.string,
		to: PropTypes.string,
		target: PropTypes.string,
	}),
	/**
	 * Renders links to appear as button and will be applied to card
	 */
	link: PropTypes.shape({
		to: PropTypes.string.isRequired,
		target: PropTypes.string,
		icon: PropTypes.shape({
			svg: PropTypes.shape({
				content: PropTypes.string,
			}),
		}),
		text: PropTypes.string,
	}),
	/**
	 * Renders heading color dynamically
	 */
	color: PropTypes.string,
	renderImage: PropTypes.func,
	renderLink: PropTypes.func,
};

PrimaryCard.defaultProps = {
	className: "",
	style: {},
	logoPosition: "bottom-left",
	subheader: "",
	content: "",
	button: null,
	link: null,
	color: "#009476",
	logo: null,
	renderImage: undefined,
	renderLink: undefined,
};

export default PrimaryCard;
export { PrimaryCard };
